import * as XLSX from 'xlsx';
import JSZip from 'jszip';

/**
 * Transform MongoDB bulk generation data to BulkResultsCard format
 * @param {Object} bulkProject - Raw bulk project data from MongoDB
 * @returns {Object} Transformed data for BulkResultsCard
 */
export const transformBulkProjectData = async (bulkProject) => {
  const { image_urls, output_files, task_id, output_json, excel_file_url } = bulkProject;

  if (!image_urls || !image_urls.parent_skus) {
    throw new Error('Missing required data in bulk project');
  }

  // Fetch product text data from output_json on S3
  let jsonData = [];
  if (output_json) {
    try {
      const resp = await fetch(output_json);
      if (resp.ok) {
        jsonData = await resp.json();
      }
    } catch (err) {
      console.error('[bulkDataTransformer] Failed to fetch output_json:', err.message);
    }
  }

  // Fall back to Excel parsing for older generations without output_json
  if (!jsonData.length) {
    let excelData = [];
    const zipUrl = excel_file_url || (output_files && output_files[0]);
    try {
      if (zipUrl) {
        const isZip = String(zipUrl).toLowerCase().endsWith('.zip');
        excelData = isZip ? await extractExcelFromZip(zipUrl) : await downloadAndParseExcel(zipUrl);
      }
    } catch (excelError) {
      console.error('[bulkDataTransformer] Failed to download/parse Excel:', excelError.message);
    }
    if (excelData.length > 0) {
    }
    jsonData = excelData.map(row => ({
      sku: row.sku,
      brand: row.brand || '',
      title: row.title || '',
      item_highlights: row.item_highlights || '',
      description: row.description || '',
      features: row.bullets || [],
      isParent: row.isParent,
      parentSku: row.parentSku || '',
    }));
  }

  // Merge any persisted sku_content_updates from the DB document
  const skuUpdates = bulkProject.sku_content_updates || {};

  // Build a lookup map from JSON data by SKU
  const jsonLookup = new Map();
  jsonData.forEach(entry => {
    if (entry.sku) jsonLookup.set(String(entry.sku).trim().toLowerCase(), entry);
  });

  // Build productData, infographicData, aplusTemplateData from image_urls
  const productData = [];
  const infographicData = {};
  const aplusTemplateData = {};
  const parentChildMap = {};
  let lastParentSku = '';

  image_urls.parent_skus.forEach((skuData) => {
    const { sku, sections, is_parent } = skuData;
    const jsonEntry = jsonLookup.get(String(sku).trim().toLowerCase()) || {};
    const updates = skuUpdates[sku];

    const isParentFromApi = is_parent === true || is_parent === 'true' || is_parent === 'TRUE';
    const isParent = jsonEntry.isParent ?? isParentFromApi;

    if (isParent) lastParentSku = sku;

    let parentSku = jsonEntry.parentSku || '';
    if (!isParent && !parentSku) parentSku = lastParentSku;

    productData.push({
      sku,
      brand: jsonEntry.brand || '',
      title: updates?.title ?? jsonEntry.title ?? `Product ${sku}`,
      item_highlights: updates?.item_highlights ?? jsonEntry.item_highlights ?? '',
      description: updates?.description ?? jsonEntry.description ?? '',
      features: updates?.bullets ?? jsonEntry.features ?? [],
      keywords_used: jsonEntry.keywords_used || null,
      isParent: !!isParent,
      parentSku: parentSku || '',
    });

    // Infographic images
    const infographicImages = [];
    if (sections.primary?.main) infographicImages.push(sections.primary.main);
    if (sections.feature) Object.values(sections.feature).forEach(url => infographicImages.push(url));
    if (sections.application) Object.values(sections.application).forEach(url => infographicImages.push(url));
    if (sections.dimension) Object.values(sections.dimension).forEach(url => infographicImages.push(url));
    infographicData[sku] = infographicImages;

    // A+ template images
    const aplusImages = [];
    if (sections.aplus) Object.values(sections.aplus).forEach(url => aplusImages.push(url));
    aplusTemplateData[sku] = aplusImages;
  });

  // Build parent-child mapping
  const childrenByParent = {};
  productData.forEach(item => {
    if (!item.isParent && item.parentSku) {
      if (!childrenByParent[item.parentSku]) childrenByParent[item.parentSku] = [];
      childrenByParent[item.parentSku].push(item);
    }
  });
  productData.forEach(item => {
    if (item.isParent) parentChildMap[item.sku] = childrenByParent[item.sku] || [];
  });

  return {
    productData,
    infographicData,
    aplusTemplateData,
    parentChildMap,
    taskId: task_id,
    downloadUrl: output_files && output_files.length > 0 ? output_files[0] : null
  };
};

// ── Excel fallback helpers (for older generations without output_json) ───────

const fetchAsArrayBuffer = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);
  return await response.arrayBuffer();
};

const parseExcelRows = (rawData) => {
  if (!rawData.length) return [];
  const columns = Object.keys(rawData[0]);
  const colMap = {};
  columns.forEach(col => { colMap[col.toLowerCase().trim()] = col; });

  const resolveCol = (candidates) => {
    for (const c of candidates) {
      if (columns.includes(c)) return c;
      const mapped = colMap[c.toLowerCase().trim()];
      if (mapped) return mapped;
    }
    return null;
  };

  const skuCol = resolveCol(['Generated Model/SKU', 'Model/SKU', 'Model Number', 'SKU', 'sku', 'ASIN']);
  const titleCol = resolveCol(['Generated Title', 'Title', 'title', 'Product Title']);
  const itemHighlightsCol = resolveCol(['Item Highlights', 'item_highlights', 'Generated Item Highlights']);
  const descCol = resolveCol(['Generated Description', 'Description', 'description']);
  const brandCol = resolveCol(['Brand', 'Brand Name', 'brand', 'brand_name']);
  const isParentCol = resolveCol(['Is Parent?', 'Is Parent', 'is_parent']);
  const parentSkuCol = resolveCol(['Parent SKU', 'parent_sku', 'Parent Model/SKU']);

  const bulletPrefixes = ['Generated Bullet', 'Bullet', 'Feature', 'Bullet Point', 'Key Feature'];
  const bulletCols = [];
  for (let i = 1; i <= 7; i++) {
    let found = null;
    for (const prefix of bulletPrefixes) {
      found = resolveCol([`${prefix} ${i}`, `${prefix}${i}`, `${prefix}_${i}`]);
      if (found) break;
    }
    bulletCols.push(found);
  }

  const getVal = (row, col) => {
    if (!col) return '';
    const v = row[col];
    return (v !== undefined && v !== null) ? String(v).trim() : '';
  };

  return rawData.map(row => {
    const bullets = [];
    for (const col of bulletCols) {
      const v = getVal(row, col);
      if (v) bullets.push(v);
    }
    const isParentRaw = getVal(row, isParentCol);
    const isParent = ['TRUE', 'true', 'Yes', 'yes'].includes(isParentRaw);
    return {
      sku: getVal(row, skuCol),
      title: getVal(row, titleCol),
      item_highlights: getVal(row, itemHighlightsCol),
      description: getVal(row, descCol),
      brand: getVal(row, brandCol),
      bullets,
      isParent,
      parentSku: getVal(row, parentSkuCol),
    };
  });
};

const downloadAndParseExcel = async (excelUrl) => {
  const buffer = await fetchAsArrayBuffer(excelUrl);
  const workbook = XLSX.read(buffer, { type: 'array' });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  return parseExcelRows(XLSX.utils.sheet_to_json(worksheet));
};

const extractExcelFromZip = async (zipUrl) => {
  const buffer = await fetchAsArrayBuffer(zipUrl);
  const zip = new JSZip();
  const zipContent = await zip.loadAsync(buffer);
  const allFiles = Object.keys(zipContent.files);
  const excelFileName = allFiles.find(f => f.endsWith('.xlsx') && f.includes('bulk_generator_results'))
    || allFiles.find(f => f.endsWith('.xlsx'));
  if (!excelFileName) return [];
  const excelFile = await zipContent.files[excelFileName].async('arraybuffer');
  const workbook = XLSX.read(excelFile, { type: 'array' });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  return parseExcelRows(XLSX.utils.sheet_to_json(worksheet));
};
